import React from 'react'
import { DropResult } from 'react-beautiful-dnd'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { debounce } from 'lodash'

import {
  DragAndDropContext,
  DragAndDropIcon,
  DraggableComponent,
  DropableBox,
  moveElement,
  moveNestedElement,
} from 'modules/core/components/DragAndDrop'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ActionsPromotionModal from 'modules/processes/components/ActionsPromotionModal'
import DeleteStepConfirmationModal from 'modules/processes/components/ProcessSettings/Steps/DeleteStepConfirmationModal'
import Items from 'modules/processes/components/ProcessSettings/Steps/Items'
import { StepDescription } from 'modules/processes/components/ProcessSettings/Steps/StepDescription'
import { useSteps } from 'modules/processes/components/ProcessSettings/Steps/StepsProvider'
import type { Step } from 'modules/processes/domain'
import { BulkUpdateItem, useBulkUpdateItems } from 'modules/processes/hooks/useBulkUpdateItems'
import { useBulkUpdateItemsAndSteps } from 'modules/processes/hooks/useBulkUpdateItemsAndSteps'
import { BulkUpdateSteps, useBulkUpdateSteps } from 'modules/processes/hooks/useBulkUpdateSteps'
import { useHover } from 'modules/processes/hooks/useHover'

export const Steps = () => {
  const navigate = useNavigate()
  const { id, category } = useParams()
  const { navigation } = useNavigation()

  const processId = Number(id)

  const {
    steps,
    stepId,
    itemsHaveChanged,
    stepsHaveChanged,
    setSteps,
    toggleUnsavedSearch,
    toggleSavedSearch,
    setStepId,
  } = useSteps()

  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)

  const { bulkUpdateSteps } = useBulkUpdateSteps()
  const { bulkUpdateItems } = useBulkUpdateItems()
  const { bulkUpdateItemsAndSteps } = useBulkUpdateItemsAndSteps()

  const { itemsToUpdate, stepsToUpdate } = useUnsafeMemo(
    () => itemsAndStepsToUpdate(steps),
    [steps]
  )

  const { performAction, running } = useAsyncAction(
    async () => {
      if (stepsHaveChanged && itemsHaveChanged) {
        bulkUpdateItemsAndSteps(itemsToUpdate, stepsToUpdate, processId)
        toggleSavedSearch('items')
        toggleSavedSearch('steps')
        return
      }

      if (stepsHaveChanged) {
        await bulkUpdateSteps(stepsToUpdate, processId)
        toggleSavedSearch('steps')
      }

      if (itemsHaveChanged) {
        await bulkUpdateItems(itemsToUpdate, processId)

        toggleSavedSearch('items')
      }
    },
    {
      successFlash: stepsHaveChanged || itemsHaveChanged,
      successFlashTime: 1000,
      loadingMessage: i18n.t('common.saving'),
    }
  )

  const debouncePerform = useDebounce(() => performAction(), 2000)

  const forcePerformDebounced = () => {
    debouncePerform.cancel()
    performAction()
  }

  const editStep = (id: number) => {
    forcePerformDebounced()
    navigate(
      navigation.processes.template.id.settings.editStep.fullPath({
        category: String(category),
        id: processId,
        stepId: id,
      })
    )
  }

  const addStep = () => {
    forcePerformDebounced()
    navigate(
      navigation.processes.template.id.settings.createStep.fullPath({
        category: String(category),
        id: processId,
      })
    )
  }

  const duplicateStep = (id: number) => {
    forcePerformDebounced()
    navigate(
      navigation.processes.template.id.settings.duplicate.fullPath({
        category: String(category),
        id: processId,
        stepToDuplicateId: id,
      })
    )
  }

  const deleteStep = (id: number) => {
    forcePerformDebounced()
    setStepId(id)
    setShowDeleteModal(true)
  }

  useUnsafeEffect(() => {
    if (itemsHaveChanged || stepsHaveChanged) debouncePerform()

    // Clean up the debounced function when the component unmounts
    return () => {
      debouncePerform.cancel() // Make sure to cancel the debounced function to prevent memory leaks & restart the timer with every change
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsHaveChanged, stepsHaveChanged])

  useVisibilityAndUnload(itemsHaveChanged || stepsHaveChanged, forcePerformDebounced)

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || running) return

    const destination = result.destination
    const origin = result.source
    const itemType = result.type
    const itemsChange = itemType === 'item'

    if (itemType === 'step' && destination.index === origin.index) return

    if (
      destination.droppableId === origin.droppableId &&
      destination.index === origin.index &&
      itemsChange
    )
      return

    setSteps((prevSteps) => {
      if (itemType === 'item') {
        // eslint-disable-next-line  no-type-assertion/no-type-assertion
        return moveNestedElement(
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          prevSteps,
          origin.index,
          destination.index,
          Number(origin.droppableId),
          Number(destination.droppableId)
        )
      }

      return moveElement(prevSteps, origin.index, destination.index)
    })

    if (itemsChange) toggleUnsavedSearch('items')

    if (itemType === 'step') toggleUnsavedSearch('steps')

    debouncePerform()
  }

  return (
    <>
      <ActionsPromotionModal />
      <DragAndDropContext onDragEnd={onDragEnd}>
        <Box gap="s24">
          <DropableBox droppableId="steps" type="step">
            <Box gap="s48">
              {steps.map((step, index) => (
                <DraggableComponent key={step.id} index={index} id={step.id?.toString() || ''}>
                  <Box
                    key={step.id}
                    gap="s12"
                    padding="s24"
                    paddingLeft="s16"
                    background="grey100"
                    borderRadius={{ all: 'abs016' }}
                  >
                    <StepHeader disabled={running}>
                      <Box flexDirection="row" justifyContent="spaceBetween">
                        <Box
                          flexDirection="row"
                          justifyContent="spaceBetween"
                          alignItems="flexStart"
                          gap="s8"
                        >
                          <Box gap="s8">
                            <Text.Header2 weight="semibold" ellipsis>
                              {step.name}
                            </Text.Header2>
                            <StepDescription
                              step={step}
                              stepProgressionCondition={String(step.progressionCondition)}
                              stepProgressionConditionResourceKey={String(
                                step.progressionConditionResourceKey
                              )}
                            />
                          </Box>
                        </Box>

                        <Box>
                          <PopoverMenu type="list">
                            <PopoverMenu.Trigger>
                              <Button.Decorative type="secondary" iconName="ellipsis" />
                            </PopoverMenu.Trigger>
                            <PopoverMenu.Content side="bottom" align="center">
                              <PopoverMenu.TouchableOption
                                icon="pencil"
                                title={i18n.t('processes.process_settings.steps.edit')}
                                onClick={() => editStep(Number(step.id))}
                              />
                              <PopoverMenu.TouchableOption
                                icon="duplicate"
                                title={i18n.t('processes.process_settings.steps.duplicate')}
                                onClick={() => duplicateStep(Number(step.id))}
                              />
                              <PopoverMenu.TouchableOption
                                icon="delete"
                                title={i18n.t('processes.process_settings.steps.delete')}
                                onClick={() => deleteStep(Number(step.id))}
                                danger
                              />
                            </PopoverMenu.Content>
                          </PopoverMenu>
                        </Box>
                      </Box>
                    </StepHeader>
                    <Box paddingLeft="s8">
                      <Items
                        stepId={Number(step.id)}
                        items={step.items}
                        processId={processId}
                        position={index}
                        updateItemAndSteps={forcePerformDebounced}
                        isEditable
                      />
                    </Box>
                  </Box>
                </DraggableComponent>
              ))}
            </Box>
          </DropableBox>

          <Box width="fit-content">
            <Button type="secondary" onClick={addStep} iconName="add">
              {i18n.t('processes.process_settings.steps.new')}
            </Button>
          </Box>

          {stepId && showDeleteModal && (
            <DeleteStepConfirmationModal
              processId={processId}
              onClose={() => setShowDeleteModal(false)}
              step={steps.find((step) => step.id === stepId)!} // eslint-disable-line no-type-assertion/no-type-assertion
            />
          )}
        </Box>
      </DragAndDropContext>
    </>
  )
}

const StepHeader = ({
  children,
  disabled = false,
}: React.PropsWithChildren<{ disabled?: boolean }>) => {
  const { actions, hover } = useHover()

  return (
    <Box flexDirection="row" {...actions} alignItems="flexStart">
      <Box paddingRight="s8">
        <DragAndDropIcon hover={hover && !disabled} />
      </Box>
      <Box width="full">{children}</Box>
    </Box>
  )
}

type DebouncedFunction<T extends (...args: unknown[]) => void> = T

const useDebounce = <T extends (...args: unknown[]) => void>(callback: T, delay: number) => {
  const ref = React.useRef<DebouncedFunction<T> | null>(null)

  useUnsafeEffect(() => {
    ref.current = callback
  }, [callback])

  const debouncedCallback = useUnsafeMemo(() => {
    const func = () => {
      ref.current?.()
    }

    return debounce(func, delay)
  }, [delay])

  return debouncedCallback
}

/**
 * Extracts dataCollectionConfiguration from taskTypeConfiguration JSON string.
 * This preserves the "require upload document" toggles when reordering tasks.
 *
 * The mapping is based on the folder configuration:
 * - identification_number:
 *   - uploadIdCertificate: presence of onboarding_folder_id
 *   - uploadSocialSecurityCertificate: presence of folder_id
 * - bank_information:
 *   - uploadBankCertificate: presence of folder_id
 */
const extractDataCollectionConfiguration = (
  taskTypeConfiguration?: string | null
): Record<string, boolean> | undefined => {
  if (!taskTypeConfiguration) return undefined

  try {
    const config = JSON.parse(taskTypeConfiguration)
    const categories = config?.configuration_fields?.at(0)?.categories || []

    const result: Record<string, boolean> = {}

    for (const category of categories) {
      const categoryType = category.category
      const configuration = category.configuration || {}

      // Map folder configurations to upload toggles
      if (categoryType === 'identification_number') {
        // uploadIdCertificate is enabled when folder_id is present
        if (configuration.folder_id !== undefined) {
          result.uploadIdCertificate = !!configuration.folder_id
        }
        // uploadSocialSecurityCertificate is enabled when onboarding_folder_id is present
        if (configuration.onboarding_folder_id !== undefined) {
          result.uploadSocialSecurityCertificate = !!configuration.onboarding_folder_id
        }
      } else if (categoryType === 'bank_information') {
        // uploadBankCertificate is enabled when folder_id is present
        if (configuration.folder_id !== undefined) {
          result.uploadBankCertificate = !!configuration.folder_id
        }
      }
    }

    return Object.keys(result).length > 0 ? result : undefined
  } catch (error) {
    // If parsing fails, return undefined to not interfere with the update
    return undefined
  }
}

const itemsAndStepsToUpdate = (
  steps: Step[]
): { itemsToUpdate: BulkUpdateItem[]; stepsToUpdate: BulkUpdateSteps[] } => ({
  itemsToUpdate: steps.flatMap((step) =>
    step.items.map(
      (item, index): BulkUpdateItem => ({
        id: item.id,
        title: item.title,
        offsetAmount: item.offsetAmount,
        offsetDirection: item.offsetDirection,
        taskTypeConfiguration: item.taskTypeConfiguration,
        referenceFieldKey: item.referenceFieldKey,
        mandatory: item.mandatory,
        instruction: item.instruction,
        assigneeIds: item.itemAssignee?.assigneeIds,
        taskType: item.taskType,
        assigneeStrategy: item.itemAssignee?.assigneeStrategy || 'affected_user',
        stepId: step.id,
        position: index + 1,
        dataCollectionConfiguration: extractDataCollectionConfiguration(item.taskTypeConfiguration),
      })
    )
  ),
  stepsToUpdate: steps.map(
    (step: Step): BulkUpdateSteps => ({
      id: Number(step.id),
      name: step.name,
      position: step.position,
    })
  ),
})

const useVisibilityAndUnload = (unSavedChanges: boolean, performAction: () => void): void => {
  useUnsafeEffect(() => {
    const handleUnloadChange = (e: Event) => {
      if (!unSavedChanges) return

      e.preventDefault()
      performAction()
      return (e.returnValue = true) // This allows to show a browser alert when refresh or close if there are some unsaved changes
    }

    const handleVisibility = () => {
      if (!unSavedChanges) return
      performAction()
    }

    // Attach event listeners for both visibilitychange and beforeunload
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('beforeunload', handleUnloadChange)

    // Remove the event listeners when the component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('beforeunload', handleUnloadChange)
    }
  }, [performAction, unSavedChanges])
}
